import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import CategoricalBarChart from 'components/CategoricalBarChart';
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import Api from "pages/AInsight/Operations/util/api";

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import btn_diagnosis from 'assets/images/icons/btn_diagnosis.png';

const DEFAULT_DISPLAY_COUNT = 2;
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const ERROR_TYPE_MAP = {
  overload: "Overload",
  shortCircuit: "Short Circuit",
  lowVoltage: "Low Voltage",
  budgetExceeded: "Budget Exceeded",
  iCutError: "I_Cut Error",
  nonStdPd: "Non-Std PD",
  notPoePort: "Non-PoE Port",
};

const PoEFaultDrawer = ({ chartData, auth }) => {
  const theme = useTheme();
  const { sendBatch } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [alertData, setAlertData] = useState([]);
  const [requestUuids] = useState({
    alerts: uuidv4(),
  });

  const fetchData = async () => {
    if (!auth && checkMockup !== 'true') return;
    setLoading(true);
    const api = Api.init(auth || {});
    const alertsParams = api.getPoEErrorPortListParams();

    const { getSendBatchData } = await sendBatch([
      { ...alertsParams, requestUuid: requestUuids.alerts },
    ]);

    const alertsRes = getSendBatchData({ requestUuid: requestUuids.alerts })?.data;
    if (alertsRes?.errorPortList) {
      setAlertData(alertsRes.errorPortList);
    } else if (alertsRes?.data?.errorPortList) {
      setAlertData(alertsRes.data.errorPortList);
    } else if (alertsRes?.devices) {
      setAlertData(alertsRes.devices);
    } else if (alertsRes?.data?.devices) {
      setAlertData(alertsRes.data.devices);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth]);

  const tableData = useMemo(() => {
    const data = alertData.map(item => ({
      devicePort: `${item.deviceName} / ${item.portId === 0 ? 'Total' : `P${item.portId}`}`,
      issue: ERROR_TYPE_MAP[item.errorType] || item.errorType
    }));
    return showAll ? data : data.slice(0, DEFAULT_DISPLAY_COUNT);
  }, [showAll, alertData]);

  const columns = useMemo(() => [
    columnHelper.accessor("devicePort", {
      header: "Device / Port",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("issue", {
      header: "Issue",
      cell: (info) => (
        <Typography sx={{ color: '#172664', fontSize: '13px' }}>
          {info.getValue()}
        </Typography>
      ),
    }),
  ], [columnHelper]);

  const table = useTableInstance({
    tableName: "poeActiveAlertsTable",
    columns: columns,
    data: tableData,
    enableSorting: false,
    enablePagination: false,
  });

  const categoricalChartData = useMemo(() => {
    if (!chartData?.errorCounts) return null;
    const { pdShort = 0, overload = 0, powerDenied = 0 } = chartData.errorCounts;

    // Calculate otherErrors similar to the dashboard component
    const { mpsAbsent = 0, notPoePort = 0, overVoltage = 0, startupFailure = 0, thermalShutdown = 0, underVoltage = 0 } = chartData.errorCounts;
    const otherErrors = mpsAbsent + notPoePort + overVoltage + startupFailure + thermalShutdown + underVoltage;

    return {
      categories: ['Short Circuit', 'Overload', 'Power Denied', 'Other Errors'],
      seriesData: [
        { value: pdShort, itemStyle: { color: '#172664' } },
        { value: overload, itemStyle: { color: '#4B68CF' } },
        { value: powerDenied, itemStyle: { color: '#87A2E2' } },
        { value: otherErrors, itemStyle: { color: '#C9DCF3' } }
      ]
    };
  }, [chartData]);

  const tooltipOption = useMemo(() => ({
    show: true,
    trigger: 'item',
    backgroundColor: 'rgba(55, 55, 55, 0.9)',
    borderColor: 'transparent',
    padding: [8, 12],
    borderRadius: 4,
    textStyle: { color: '#fff' },
    formatter: (params) => {
      return `
        <div style="font-size: 12px; line-height: 1.6;">
          <div style="opacity: 0.9;">${params.name}</div>
          <div>Occurrences : <span style="font-weight: bold;">${params.value}</span></div>
        </div>
      `;
    }
  }), []);

  const statusCards = useMemo(() => {
    if (!chartData) {
      return [
        { label: 'Normal', value: '0%', sub: 'Power < 80%' },
        { label: 'Overload Risk', value: '0%', sub: '80% ≤ Power < 95%' },
        { label: 'Failures', value: '0%', sub: 'Power ≥ 95%' }
      ];
    }

    const { normalPercent = 0, errorCounts = {}, totalPoePort = 1 } = chartData;
    const { overload = 0, startupFailure = 0 } = errorCounts;

    const riskPercent = Math.round((overload / totalPoePort) * 100);
    const failPercent = Math.round((startupFailure / totalPoePort) * 100);

    return [
      { label: 'Normal', value: `${normalPercent}%`, sub: 'Power < 80%' },
      { label: 'Overload Risk', value: `${riskPercent}%`, sub: '80% ≤ Power < 95%' },
      { label: 'Failures', value: `${failPercent}%`, sub: 'Power ≥ 95%' }
    ];
  }, [chartData]);
  const showMore = alertData.length > DEFAULT_DISPLAY_COUNT;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_linebar_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          PoE Status Detection
        </Typography>
      </Box>

      {/* Categorical Chart */}
      <CollapseCard title="PoE Power Distribution">
        <Box sx={{ width: "100%", height: "250px", pt: 2 }}>
          {categoricalChartData ? (
            <CategoricalBarChart chartData={categoricalChartData} height="230px" tooltipCustomOption={tooltipOption} />
          ) : (
            <Typography>No data available</Typography>
          )}
        </Box>
      </CollapseCard>

      {/* Status Cards */}
      {/* 
      <Grid container spacing={2}>
        {statusCards.map((item, index) => (
          <Grid item xs={4} key={index}>
            <Box sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#172664' }}>{item.label}</Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#172664' }}>{item.value}</Typography>
              <Typography sx={{ fontSize: '11px', color: '#718096' }}>{item.sub}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      */}

      {/* Active Alerts */}
      <CollapseCard title="Active PoE Alerts(Last 24 Hours)">
        <Box sx={{ width: "100%" }}>
          <TableField {...table} loading={loading} />
          {showMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Typography
                onClick={() => setShowAll(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0, padding: 0, color: theme.palette.primary.greySecound, cursor: "pointer",
                }}
              >
                {showAll ? "Show Less" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>

      {/* Root Cause Analysis - Dark Mode */}
      {/* 
      <Box sx={{
        bgcolor: '#5C5C5C',
        borderRadius: '8px',
        p: 2.5,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src={btn_diagnosis} sx={{ width: '24px', height: '24px' }} />
          <Typography sx={{ fontWeight: 'bold', fontSize: '15px' }}>Root Cause Analysis</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>Critical Hardware Fault</Typography>
          <Typography sx={{ fontSize: '12px', opacity: 0.9, lineHeight: 1.5 }}>
            Short circuit protection detected on SW-Lobby-02 Port 08. The port has been automatically shut down to protect the switch. Affected device: Front Desk IP Cam.
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>Suggested Actions:</Typography>
          <Typography component="div" sx={{ fontSize: '12px', opacity: 0.9, lineHeight: 1.6 }}>
            • Cable Test: Perform a TDR (Time Domain Reflectometry) test to identify potential short circuits in the network cable.<br />
            • Device Check: Inspect the end PD (Powered Device) for signs of water ingress or physical damage.
          </Typography>
        </Box>
      </Box>
      */}
    </Box>
  );
};

export default PoEFaultDrawer;
